import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { MANUFACTURER_FINANCE } from '../../data/manufacturerFinance';
import { Factory, X, Check, Gift, Package, Sparkles, Calendar, ChevronRight } from 'lucide-react';

/**
 * Annual Trade Show — modal that pops every 360 in-game days when at least
 * one manufacturer relation is active. For each booth (brand), the player
 * can accept ONE of:
 *   - a 30-day demo of a tier-up vehicle (free)
 *   - a bulk order of 3 vehicles of a standard model at -8 % (paid cash now)
 *   - skip the booth
 *
 * Decisions accumulate inside pendingMfgTradeShow.decisions until the
 * player closes the modal.
 */
export const TradeShowModal = () => {
  const show = useGameStore(s => s.pendingMfgTradeShow);
  const acceptDemo = useGameStore(s => s.acceptTradeShowDemo);
  const acceptBundle = useGameStore(s => s.acceptTradeShowBundle);
  const skipBooth = useGameStore(s => s.skipTradeShowBooth);
  const closeShow = useGameStore(s => s.closeTradeShow);
  const treasury = useGameStore(s => s.treasury);

  if (!show || !show.active) return null;

  return (
    <div
      className="fixed inset-0 z-[96] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="trade-show-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-4xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200 max-h-[92vh] flex flex-col"
        data-testid="trade-show-modal"
      >
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <div>
              <h3 className="font-bold text-lg">Salon du Transport {show.year}</h3>
              <p className="text-[11px] italic opacity-90">L'événement annuel des constructeurs — offres exclusives pour nos clients fidèles</p>
            </div>
          </div>
          <button aria-label="Fermer" onClick={closeShow} className="text-white/70 hover:text-white" data-testid="trade-show-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="rounded-md bg-accent/10 border border-accent/30 px-3 py-2 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-slate-300 leading-relaxed">
              Chaque constructeur avec lequel vous avez tissé une relation propose 2 offres exclusives.
              Vous pouvez accepter <strong className="text-accent">au maximum 1 offre par stand</strong>, ou passer votre chemin.
              Tous les véhicules de démonstration sont restitués au bout de {30} jours.
            </p>
          </div>

          <div className="space-y-3">
            {show.brandOffers.map(offer => {
              const decision = show.decisions?.[offer.brandId];
              const canAffordBundle = treasury >= offer.bundle.totalDiscountedPrice;
              return (
                <div
                  key={offer.brandId}
                  className="rounded-lg border-2 overflow-hidden"
                  style={{ borderColor: `${offer.brandColor}40` }}
                  data-testid={`booth-${offer.brandId}`}
                >
                  <div
                    className="px-4 py-2.5 flex items-center justify-between"
                    style={{ background: `linear-gradient(135deg, ${offer.brandColor} 0%, ${offer.brandColor}DD 100%)` }}
                  >
                    <div className="flex items-center gap-2 text-white">
                      <Factory className="w-4 h-4" />
                      <div>
                        <p className="font-bold text-sm">{offer.brandName}</p>
                        <p className="text-[10px] italic opacity-90">{offer.tagline}</p>
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/15 text-white font-mono">
                      Statut : {offer.loyaltyLabel} · {offer.units}u
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-ink-700/20">
                    {/* DEMO offer */}
                    <div className={`rounded-md border p-3 ${decision === 'demo' ? 'border-money/60 bg-money/5' : 'border-ink-600 bg-ink-700/30'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="w-3.5 h-3.5 text-money" />
                        <p className="text-[11px] uppercase tracking-wider text-money font-mono font-bold">Démonstration 30j</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-100">{offer.demo.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Tier {offer.demo.tier} · Test grandeur nature</p>
                      <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">
                        Le constructeur vous prête un véhicule {offer.demo.tier > 1 ? 'haut de gamme' : ''} pendant 30 jours.
                        Aucun engagement, restitution automatique. Affectez un chauffeur et générez du revenu pendant la période !
                      </p>
                      {decision === 'demo' ? (
                        <div className="mt-2 text-[11px] text-money font-bold flex items-center gap-1" data-testid={`decision-demo-${offer.brandId}`}>
                          <Check className="w-3 h-3" /> Démo acceptée
                        </div>
                      ) : decision ? (
                        <div className="mt-2 text-[11px] text-slate-500 italic">Offre passée</div>
                      ) : (
                        <button
                          onClick={() => acceptDemo(offer.brandId)}
                          className="mt-2 w-full bg-money text-ink-900 px-3 py-1.5 rounded text-xs font-bold hover:bg-money/85 transition-colors flex items-center justify-center gap-1.5"
                          data-testid={`accept-demo-${offer.brandId}`}
                        >
                          <Check className="w-3 h-3" /> Prendre la démo (gratuit)
                        </button>
                      )}
                    </div>

                    {/* BUNDLE offer */}
                    <div className={`rounded-md border p-3 ${decision === 'bundle' ? 'border-accent/60 bg-accent/5' : 'border-ink-600 bg-ink-700/30'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-3.5 h-3.5 text-accent" />
                        <p className="text-[11px] uppercase tracking-wider text-accent font-mono font-bold">Commande groupée -8%</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-100">3 × {offer.bundle.label}</p>
                      <div className="text-[11px] text-slate-400 mt-1 space-y-0.5">
                        <p>Prix catalogue : <span className="line-through">{formatMoney(offer.bundle.totalListPrice)}</span></p>
                        <p>Prix salon : <strong className="text-accent">{formatMoney(offer.bundle.totalDiscountedPrice)}</strong> <span className="text-money">(-{formatMoney(offer.bundle.totalListPrice - offer.bundle.totalDiscountedPrice)})</span></p>
                      </div>
                      {decision === 'bundle' ? (
                        <div className="mt-2 text-[11px] text-accent font-bold flex items-center gap-1" data-testid={`decision-bundle-${offer.brandId}`}>
                          <Check className="w-3 h-3" /> Commande validée
                        </div>
                      ) : decision ? (
                        <div className="mt-2 text-[11px] text-slate-500 italic">Offre passée</div>
                      ) : (
                        <button
                          onClick={() => acceptBundle(offer.brandId)}
                          disabled={!canAffordBundle}
                          className="mt-2 w-full bg-accent text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-accent-hover transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`accept-bundle-${offer.brandId}`}
                          title={!canAffordBundle ? `Trésorerie insuffisante (besoin ${formatMoney(offer.bundle.totalDiscountedPrice)})` : undefined}
                        >
                          <Check className="w-3 h-3" />
                          {canAffordBundle ? 'Signer la commande' : 'Trésorerie insuffisante'}
                        </button>
                      )}
                    </div>
                  </div>

                  {!decision && (
                    <div className="px-3 pb-3">
                      <button
                        onClick={() => skipBooth(offer.brandId)}
                        className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
                        data-testid={`skip-booth-${offer.brandId}`}
                      >
                        Passer ce stand →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between flex-shrink-0">
          <p className="text-[10px] text-slate-500">
            Décisions prises : {Object.keys(show.decisions || {}).length} / {show.brandOffers.length}
          </p>
          <button
            onClick={closeShow}
            className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors flex items-center gap-2"
            data-testid="close-trade-show"
          >
            Quitter le salon
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeShowModal;
